import React from "react";
import { Helmet } from "react-helmet";
import { Typography } from "@mui/material";
import Footer from "../Components/Footer";
import { LegalLayout, Section, Paragraph, BulletList, LegalPageLink } from "../Components/Legal/LegalLayout";

export default function TermsAndConditions() {
  return (
    <div>
      <Helmet>
        <title>Terms and Conditions | GroHair & GloSkin Dindigul</title>
        <meta
          name="description"
          content="Read the Terms and Conditions for using the GroHair & GloSkin Dindigul website and services."
        />
        <meta property="og:title" content="Terms and Conditions | GroHair & GloSkin Dindigul" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://adgrohairgloskindindigul.in/terms-and-conditions" />
      </Helmet>

      <LegalLayout
        title="Terms and Conditions"
        lastUpdated="August 28, 2026"
        intro="Please read these terms and conditions carefully before using Our Service."
      >
        <Section heading="Interpretation and Definitions">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
            Interpretation
          </Typography>
          <Paragraph>
            The words whose initial letters are capitalized have meanings defined under the
            following conditions. The following definitions shall have the same meaning regardless
            of whether they appear in singular or in plural.
          </Paragraph>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
            Definitions
          </Typography>
          <Paragraph>For the purposes of these Terms and Conditions:</Paragraph>
          <BulletList
            items={[
              <>
                <strong>Affiliate</strong> means an entity that controls, is controlled by, or is
                under common control with a party, where "control" means ownership of 50% or more of
                the shares, equity interest or other securities entitled to vote for election of
                directors or other managing authority.
              </>,
              <>
                <strong>Country/State</strong> refers to: Tamil Nadu, India.
              </>,
              <>
                <strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our"
                in these Terms and Conditions) refers to Advanced GroHair & GloSkin – Dindigul, 1st
                floor, 2, UTHCHAV MALL, 6, Trichy - Dindigul Rd, near City Hospital, Gandhiji Nagar,
                Cooperative Nagar, Dindigul, Tamil Nadu 624001.
              </>,
              <>
                <strong>Device</strong> means any device that can access the Service such as a
                computer, a cell phone or a digital tablet.
              </>,
              <>
                <strong>Service</strong> refers to the Website.
              </>,
              <>
                <strong>Terms and Conditions</strong> (also referred to as "Terms") means these
                Terms and Conditions, including any documents expressly incorporated by reference,
                which govern Your access to and use of the Service and form the entire agreement
                between You and the Company regarding the Service.
              </>,
              <>
                <strong>Third-Party Social Media Service</strong> means any services or content
                (including data, information, products or services) provided by a third party that
                is displayed, included, made available, or linked to through the Service.
              </>,
              <>
                <strong>Website</strong> refers to GroHair & GloSkin Dindigul, accessible from{" "}
                <a href="https://adgrohairgloskindindigul.in/" style={{ color: "#e3242b" }}>
                  https://adgrohairgloskindindigul.in/
                </a>
                .
              </>,
              <>
                <strong>You</strong> means the individual accessing or using the Service, or the
                company, or other legal entity on behalf of which such individual is accessing or
                using the Service, as applicable.
              </>,
            ]}
          />
        </Section>

        <Section heading="Acknowledgment">
          <Paragraph>
            These are the Terms and Conditions governing the use of this Service and the agreement
            between You and the Company. These Terms and Conditions set out the rights and
            obligations of all users regarding the use of the Service.
          </Paragraph>
          <Paragraph>
            Your access to and use of the Service is conditioned on Your acceptance of and
            compliance with these Terms and Conditions. These Terms and Conditions apply to all
            visitors, users and others who access or use the Service.
          </Paragraph>
          <Paragraph>
            By accessing or using the Service You agree to be bound by these Terms and Conditions.
            If You disagree with any part of these Terms and Conditions then You may not access the
            Service.
          </Paragraph>
          <Paragraph>
            You represent that you are over the age of 18. The Company does not permit those under
            18 to use the Service.
          </Paragraph>
          <Paragraph>
            Your access to and use of the Service is also subject to Our{" "}
            <LegalPageLink to="/privacy-policy">Privacy Policy</LegalPageLink>, which describes how
            We collect, use, and disclose personal information. Please read Our Privacy Policy
            carefully before using Our Service.
          </Paragraph>
        </Section>

        <Section heading="Links to Other Websites">
          <Paragraph>
            Our Service may contain links to third-party websites or services that are not owned or
            controlled by the Company. The Company has no control over, and assumes no
            responsibility for, the content, privacy policies, or practices of any third-party
            websites or services. You further acknowledge and agree that the Company shall not be
            responsible or liable, directly or indirectly, for any damage or loss caused or alleged
            to be caused by or in connection with the use of or reliance on any such content, goods
            or services available on or through any such websites or services.
          </Paragraph>
          <Paragraph>
            We strongly advise You to read the terms and conditions and privacy policies of any
            third-party websites or services that You visit.
          </Paragraph>
        </Section>

        <Section heading="Links from a Third-Party Social Media Service">
          <Paragraph>
            The Service may display, include, make available, or link to content or services
            provided by a Third-Party Social Media Service. A Third-Party Social Media Service is
            not owned or controlled by the Company, and the Company does not endorse or assume
            responsibility for any Third-Party Social Media Service.
          </Paragraph>
          <Paragraph>
            You acknowledge and agree that the Company shall not be responsible or liable, directly
            or indirectly, for any damage or loss caused or alleged to be caused by or in connection
            with Your access to or use of any Third-Party Social Media Service, including any
            content, goods, or services made available through them. Your use of any Third-Party
            Social Media Service is governed by that Third-Party Social Media Service's terms and
            privacy policies.
          </Paragraph>
        </Section>

        <Section heading="Termination">
          <Paragraph>
            We may terminate or suspend Your access immediately, without prior notice or liability,
            for any reason whatsoever, including without limitation if You breach these Terms and
            Conditions. Upon termination, Your right to use the Service will cease immediately.
          </Paragraph>
        </Section>

        <Section heading="Limitation of Liability">
          <Paragraph>
            Notwithstanding any damages that You might incur, the entire liability of the Company
            and any of its suppliers under any provision of these Terms and Your exclusive remedy
            for all of the foregoing shall be limited to the amount actually paid by You through the
            Service, or, if You haven't purchased anything through the Service, to the minimal
            extent permitted under applicable law.
          </Paragraph>
          <Paragraph>
            To the maximum extent permitted by applicable law, in no event shall the Company or its
            suppliers be liable for any special, incidental, indirect, or consequential damages
            whatsoever (including, but not limited to, damages for loss of profits, loss of data or
            other information, for business interruption, for personal injury, loss of privacy
            arising out of or in any way related to the use of or inability to use the Service),
            even if the Company or any supplier has been advised of the possibility of such damages
            and even if the remedy fails of its essential purpose.
          </Paragraph>
          <Paragraph>
            Some jurisdictions do not allow the exclusion of implied warranties or limitation of
            liability for incidental or consequential damages, which means that some of the above
            limitations may not apply. In such jurisdictions, each party's liability will be limited
            to the greatest extent permitted by law.
          </Paragraph>
        </Section>

        <Section heading='"AS IS" and "AS AVAILABLE" Disclaimer'>
          <Paragraph>
            The Service is provided to You "AS IS" and "AS AVAILABLE" and with all faults and
            defects without warranty of any kind. To the maximum extent permitted under applicable
            law, the Company, on its own behalf and on behalf of its Affiliates and its and their
            respective licensors and service providers, expressly disclaims all warranties, whether
            express, implied, statutory or otherwise, with respect to the Service, including all
            implied warranties of merchantability, fitness for a particular purpose, title and
            non-infringement, and warranties that may arise out of course of dealing, course of
            performance, usage or trade practice.
          </Paragraph>
          <Paragraph>
            Without limitation to the foregoing, the Company provides no warranty or undertaking,
            and makes no representation of any kind that the Service will meet Your requirements,
            achieve any intended results, operate without interruption, meet any performance or
            reliability standards or be error free or that any errors or defects can or will be
            corrected.
          </Paragraph>
          <Paragraph>
            Some jurisdictions do not allow the exclusion of certain types of warranties or
            limitations on applicable statutory rights of a consumer, so some or all of the above
            exclusions and limitations may not apply to You. But in such a case the exclusions and
            limitations set forth in this section shall be applied to the greatest extent
            enforceable under applicable law.
          </Paragraph>
        </Section>

        <Section heading="Governing Law">
          <Paragraph>
            The laws of Tamil Nadu, India, excluding its conflicts of law rules, shall govern these
            Terms and Your use of the Service. Your use of the Service may also be subject to other
            local, state, national, or international laws.
          </Paragraph>
        </Section>

        <Section heading="Disputes Resolution">
          <Paragraph>
            If You have any concern or dispute about the Service, You agree to first try to resolve
            the dispute informally by contacting the Company. Any dispute that cannot be resolved
            informally shall be subject to the exclusive jurisdiction of the courts at Dindigul,
            Tamil Nadu.
          </Paragraph>
        </Section>

        <Section heading="Severability and Waiver">
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Severability</Typography>
          <Paragraph>
            If any provision of these Terms is held to be unenforceable or invalid, such provision
            will be changed and interpreted to accomplish the objectives of such provision to the
            greatest extent possible under applicable law and the remaining provisions will continue
            in full force and effect.
          </Paragraph>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Waiver</Typography>
          <Paragraph>
            Except as provided herein, the failure to exercise a right or to require performance of
            an obligation under these Terms shall not affect a party's ability to exercise such
            right or require such performance at any time thereafter, nor shall the waiver of a
            breach constitute a waiver of any subsequent breach.
          </Paragraph>
        </Section>

        <Section heading="Changes to These Terms and Conditions">
          <Paragraph>
            We reserve the right, at Our sole discretion, to modify or replace these Terms at any
            time. If a revision is material, We will make reasonable efforts to provide at least 30
            days' notice prior to any new terms taking effect. What constitutes a material change
            will be determined at Our sole discretion.
          </Paragraph>
          <Paragraph>
            By continuing to access or use Our Service after those revisions become effective, You
            agree to be bound by the revised terms. If You do not agree to the new terms, in whole
            or in part, please stop using the Service.
          </Paragraph>
        </Section>

        <Section heading="Contact Us">
          <Paragraph>
            If you have any questions about these Terms and Conditions, You can contact us:
          </Paragraph>
          <BulletList
            items={[
              "By email: adgrohairdgl@gmail.com",
              "By phone: +91 082701 56789",
            ]}
          />
        </Section>
      </LegalLayout>

      <Footer />
    </div>
  );
}
